<template>
  <textarea
    ref="input"
    :value="formatValue"
    :class="allClasses"
    v-bind="attrs"
    v-on="listeners"
    @input="onInput"
  />
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

export const AUTOSCALE_MAX_HEIGHT = 100;
export const sizes = ['m', 'l'];

export default {
  name: 'ETextarea',
  mixins: [classes],
  props: {
    model: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    name: {
      type: [String, Number],
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    autoscale: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    size: {
      type: String,
      default: 'm',
      cssClass: true,
      validator: (value) => sizes.includes(value)
    }
  },
  data() {
    return {
      formatValue: this.value,
      dirty: false,
    };
  },
  computed: {
    attrs() {
      const { name, required, disabled, $attrs } = this;
      return { ...$attrs, name, required, disabled };
    },
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    allClasses() {
      const allClasses = this.classes;
      if (this.dirty) {
        allClasses.push('textarea--dirty');
      }
      return allClasses;
    },
  },
  watch: {
    value(value) {
      this.formatValue = value;
    },
  },
  methods: {
    onInput({ target: { value } }) {
      this.formatValue = value;
      this.dirty = true;
      this.$emit('input', value);
      if (this.autoscale) {
        this.setHeight();
      }
    },
    async setHeight() {
      this.$el.style.height = '';
      const { scrollHeight } = this.$el;
      const height = Math.min(scrollHeight, AUTOSCALE_MAX_HEIGHT);
      await this.$nextTick();
      this.$el.style.height = `${height}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
$sizes: (
  m: (
    font-size: $font-m,
    padding: $space-s,
    height: $space-3l,
  ),
  l: (
    font-size: $font-l,
    padding: $space-m,
    height: $space-4l,
  )
);

.textarea {
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: $space-5l;
  border: solid $border-width $color-border;
  border-radius: $border-radius-s;
  font-size: $font-m;
  font-family: $family;
  line-height: $line-height-s;
  color: $color-text-primary;
  resize: vertical;
  transition: border $timing-2s $easing;

  &:hover {
    border-color: $color-border-hover-primary;
  }

  &:focus {
    @extend %keyboard-nav-focus;

    border-color: $color-border-focus-primary;
  }

  &:disabled {
    color: $color-disabled;
    background-color: $white;
  }

  &::placeholder {
    color: $color-text-secondary;
    opacity: $opacity-m;
  }
}

.textarea--dirty:invalid,
.textarea--invalid {
  border-color: $color-error;
}

@each $name, $map in $sizes {
  .textarea--size-#{$name} {
    font-size: map-get($map, font-size);
    padding: map-get($map, padding);
  }

  .textarea--autoscale {
    height: map-get($map, height);
    min-height: map-get($map, height);
  }
}
</style>

<docs>
A textarea

### States

#### Default

```js
let m;
<div>
  <ETextarea
    name="test"
    v-model="m"
    placeholder="Label"
    required
  />
  <br/>
  <div>Value is {{m}}</div>
</div>
```

#### Disabled
```js
<ETextarea
  name="test"
  value="This is a value"
  placeholder="Label"
  disabled
/>
```


#### Invalid
```js
<ETextarea
  name="test"
  value="This is a value"
  placeholder="Label"
  invalid
/>
```


#### Autoscale
```js
<ETextarea
  name="test"
  value="This is a value"
  placeholder="Label"
  autoscale
/>
```
</docs>
